// ============================================================
//  src/dashboard.js — Renderizado del dashboard de concursos
// ============================================================

import { eliminarEdicion } from './firestore.js';
import { abrirModalEditar } from './modal.js';
import { mostrarToast, mostrarConfirmacion, escapeHTML } from './ui.js';
import { abrirModalArchivos } from './modal-drive.js';
import { t } from './i18n.js';
import { safeUrl } from './security.js';
import {
    renderizarTarjetaResumenPerfil,
    limpiarTarjetaResumenPerfil
} from './dashboard-profile-card.js';
import { getPerfilActual } from './perfil.js';

// ============================================================
//  Estado interno
// ============================================================

let ultimaListaEdiciones = [];
let _uid = null;

// ============================================================
//  Tarjeta "Primeros pasos"
// ============================================================

function _resaltarTemporal(el) {
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('pp-resaltado');
    setTimeout(() => el.classList.remove('pp-resaltado'), 1100);
}

function _accionPaso(num, listaEdiciones) {
    switch (num) {
        case 1: {
            const contenedor = document.getElementById('dashboard-profile-card-container');
            contenedor?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setTimeout(() => {
                const btn = contenedor?.querySelector('.btn-editar-tarjeta');
                _resaltarTemporal(btn || contenedor);
            }, 350);
            break;
        }
        case 2: {
            document.getElementById('btn-configuracion')?.click();
            setTimeout(() => {
                const seccion = document
                    .querySelector('[data-i18n="perfil.seccion.almacenamiento"]')
                    ?.closest('.pref-grupo');
                if (seccion) _resaltarTemporal(seccion);
            }, 450);
            break;
        }
        case 3:
            document.getElementById('btn-nuevo-concurso')?.click();
            break;
        case 4: {
            if (!listaEdiciones.length) break;
            const primera = document.querySelector('.tarjeta-concurso');
            if (!primera) break;
            primera.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => {
                const archivos = primera.querySelector('.tarjeta-concurso__archivos');
                _resaltarTemporal(archivos || primera);
            }, 350);
            break;
        }
        case 5: {
            const cs = getPerfilActual()?.indicativoOperador?.trim().toUpperCase();
            if (cs) {
                window.open(`https://app.contestmanager.uy/u/${encodeURIComponent(cs)}`, '_blank', 'noopener,noreferrer');
            } else {
                document.getElementById('btn-configuracion')?.click();
            }
            break;
        }
        case 6:
            document.getElementById('btn-abrir-shack')?.click();
            break;
    }
}

function crearTarjetaPrimerosPasos(uid, listaEdiciones) {

    if (!uid) return null;
    if (localStorage.getItem(`cm_primeros_pasos_cerrado_${uid}`)) return null;

    const card = document.createElement('div');
    card.className = 'primeros-pasos-card';

    const cabecera = document.createElement('div');
    cabecera.className = 'primeros-pasos-card__cabecera';

    const titulo = document.createElement('span');
    titulo.className = 'primeros-pasos-card__titulo';
    titulo.dataset.i18n = 'primeros-pasos.titulo';
    titulo.textContent = t('primeros-pasos.titulo');

    const btnCerrar = document.createElement('button');
    btnCerrar.className = 'primeros-pasos-card__cerrar';
    btnCerrar.dataset.i18n = 'primeros-pasos.cerrar';
    btnCerrar.dataset.i18nAria = 'primeros-pasos.cerrar';
    btnCerrar.textContent = t('primeros-pasos.cerrar');
    btnCerrar.setAttribute('aria-label', t('primeros-pasos.cerrar'));
    btnCerrar.addEventListener('click', () => {
        localStorage.setItem(`cm_primeros_pasos_cerrado_${uid}`, '1');
        card.remove();
    });

    cabecera.append(titulo, btnCerrar);

    const lista = document.createElement('ol');
    lista.className = 'primeros-pasos-card__lista';

    const tienesConcursos = listaEdiciones.length > 0;

    const PASOS = [
        { num: 1, clicable: true },
        { num: 2, clicable: true },
        { num: 3, clicable: true },
        { num: 4, clicable: tienesConcursos },
        { num: 5, clicable: true },
        { num: 6, clicable: true },
    ];

    PASOS.forEach(({ num, clicable }) => {
        const item = document.createElement('li');
        item.className = 'primeros-pasos-card__paso';

        if (clicable) {
            const btn = document.createElement('button');
            btn.className = 'primeros-pasos-card__paso-titulo primeros-pasos-card__paso-titulo--link';
            btn.dataset.i18n = `primeros-pasos.p${num}.titulo`;
            btn.textContent = t(`primeros-pasos.p${num}.titulo`);
            btn.addEventListener('click', () => _accionPaso(num, listaEdiciones));
            item.appendChild(btn);
        } else {
            const span = document.createElement('span');
            span.className = 'primeros-pasos-card__paso-titulo';
            span.dataset.i18n = `primeros-pasos.p${num}.titulo`;
            span.textContent = t(`primeros-pasos.p${num}.titulo`);
            item.appendChild(span);
        }

        const desc = document.createElement('p');
        desc.className = 'primeros-pasos-card__paso-desc';
        desc.dataset.i18n = `primeros-pasos.p${num}.desc`;
        desc.textContent = t(`primeros-pasos.p${num}.desc`);
        item.appendChild(desc);

        lista.appendChild(item);
    });

    card.append(cabecera, lista);
    return card;
}

// ============================================================
//  Clasificación de concursos
// ============================================================

function clasificarEdiciones(ediciones) {

    const ahora = new Date();

    return ediciones.reduce(
        (grupos, edicion) => {

            const { fechaInicio, fechaFin } = edicion;

            if (fechaInicio && fechaFin) {

                if (ahora >= fechaInicio && ahora <= fechaFin) {
                    grupos.enCurso.push(edicion);
                }
                else if (ahora < fechaInicio) {
                    grupos.proximos.push(edicion);
                }
                else {
                    grupos.finalizados.push(edicion);
                }

            } else {
                grupos.finalizados.push(edicion);
            }

            return grupos;

        },
        { enCurso: [], proximos: [], finalizados: [] }
    );
}

// ============================================================
//  Formateo de fechas
// ============================================================

function formatearFecha(fecha) {

    if (!fecha) return '—';

    return fecha.toLocaleDateString('es-UY', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });

}

// ============================================================
//  Construcción tarjeta DOM
// ============================================================

function crearTarjetaEdicion(edicion, estado) {

    const etiquetasModo = {
        SSB: 'badge--azul',
        CW: 'badge--verde',
        FT8: 'badge--naranja',
        FM: 'badge--gris',
    };

    const tieneCertificado = Number(edicion.cantidadCertificados || 0) > 0;

    const tarjeta = document.createElement('article');
    tarjeta.className =
        `tarjeta-concurso tarjeta-concurso--${estado} ` +
        (tieneCertificado ? 'tarjeta-concurso--certificado-ok' : 'tarjeta-concurso--certificado-pendiente');
    tarjeta.dataset.id = edicion.id || '';

    const cabecera = document.createElement('div');
    cabecera.className = 'tarjeta-concurso__cabecera';

    const tituloGrupo = document.createElement('div');
    tituloGrupo.className = 'tarjeta-concurso__titulo-grupo';

    const nombre = document.createElement('h3');
    nombre.className = 'tarjeta-concurso__nombre';
    nombre.textContent = edicion.concurso || '—';

    const año = document.createElement('span');
    año.className = 'tarjeta-concurso__año';
    año.textContent = edicion.año || '—';

    tituloGrupo.append(nombre, año);

    const acciones = document.createElement('div');
    acciones.className = 'tarjeta-concurso__acciones-cabecera';

    const badge = document.createElement('span');
    badge.className = `badge ${etiquetasModo[edicion.modo] ?? 'badge--gris'}`;
    badge.textContent = edicion.modo || '—';

    const btnEditar = document.createElement('button');
    btnEditar.className = 'btn-accion btn-accion--editar';
    btnEditar.dataset.id = edicion.id;
    btnEditar.title = t('tarjeta.btn.editar');
    btnEditar.dataset.i18nAria = 'tarjeta.btn.editar';
    btnEditar.setAttribute('aria-label', t('tarjeta.btn.editar'));
    btnEditar.textContent = '✏️';

    const btnEliminar = document.createElement('button');
    btnEliminar.className = 'btn-accion btn-accion--eliminar';
    btnEliminar.dataset.id = edicion.id;
    btnEliminar.title = t('tarjeta.btn.eliminar');
    btnEliminar.dataset.i18nAria = 'tarjeta.btn.eliminar';
    btnEliminar.setAttribute('aria-label', t('tarjeta.btn.eliminar'));
    btnEliminar.textContent = '🗑️';

    acciones.append(badge, btnEditar, btnEliminar);
    cabecera.append(tituloGrupo, acciones);

    const fechas = document.createElement('div');
    fechas.className = 'tarjeta-concurso__fechas';

    const inicio = document.createElement('span');
    inicio.textContent = `📅 ${formatearFecha(edicion.fechaInicio)}`;

    const separador = document.createElement('span');
    separador.className = 'tarjeta-concurso__separador';
    separador.textContent = '→';

    const fin = document.createElement('span');
    fin.textContent = formatearFecha(edicion.fechaFin);

    fechas.append(inicio, separador, fin);

    const pie = document.createElement('div');
    pie.className = 'tarjeta-concurso__pie';

    if (edicion.posicion) {

        const contPos = document.createElement('div');
        contPos.className = 'tarjeta-concurso__posicion';

        ['local', 'regional', 'mundial'].forEach(nivel => {

            const p = edicion.posicion?.[nivel];

            if (p?.puesto) {

                const item = document.createElement('span');
                item.className = 'posicion-item';

                const puesto = document.createElement('span');
                puesto.className = 'posicion-item__puesto';
                puesto.textContent = `${p.puesto}°`;

                item.appendChild(puesto);

                if (p.ambito) {

                    const amb = document.createElement('span');
                    amb.className = 'posicion-item__ambito';
                    amb.textContent = String(p.ambito).toUpperCase();

                    item.appendChild(amb);

                }

                contPos.appendChild(item);

            }

        });

        pie.appendChild(contPos);

    }

    if (edicion.finalScore) {

        const puntaje = document.createElement('span');
        puntaje.className = 'tarjeta-concurso__puntaje';
        puntaje.textContent = `${edicion.finalScore.toLocaleString('es-UY')} pts`;

        pie.appendChild(puntaje);

    }

    const url = safeUrl(edicion.webUrl);

    if (url) {

        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.className = 'tarjeta-concurso__link';
        link.dataset.i18n = 'tarjeta.btn.ver-sitio';
        link.textContent = t('tarjeta.btn.ver-sitio');

        pie.appendChild(link);

    }

    const archivos = document.createElement('div');
    archivos.className = 'tarjeta-concurso__archivos';

    const estadoCertificado = document.createElement('span');
    estadoCertificado.className =
        `badge tarjeta-concurso__estado-certificado ${tieneCertificado ? 'badge--verde' : 'badge--naranja'}`;
    estadoCertificado.dataset.i18n = tieneCertificado
        ? 'tarjeta.certificado.cargado'
        : 'tarjeta.certificado.pendiente';
    estadoCertificado.textContent = tieneCertificado
        ? t('tarjeta.certificado.cargado')
        : t('tarjeta.certificado.pendiente');

    const botonesArchivo = document.createElement('div');
    botonesArchivo.className = 'tarjeta-concurso__archivos-botones';

    const tipos = ['log', 'conf', 'res', 'cert', 'reg'];

    tipos.forEach(tipo => {

        const btn = document.createElement('button');

        btn.className = 'btn-archivo';
        btn.dataset.id = edicion.id;
        btn.dataset.tipo = tipo;

        btn.title = t(`tipo.${tipo}.nombre`);
        btn.dataset.i18nAria = `tipo.${tipo}.nombre`;
        btn.setAttribute('aria-label', t(`tipo.${tipo}.nombre`));
        btn.textContent = t(`tarjeta.archivo.${tipo}`);

        botonesArchivo.appendChild(btn);

    });

    archivos.append(estadoCertificado, botonesArchivo);

    tarjeta.append(cabecera, fechas, pie, archivos);

    return tarjeta;

}

// ============================================================
//  Secciones
// ============================================================

function crearSeccion(titulo, ediciones, estado) {

    const section = document.createElement('section');
    section.className = 'seccion-concursos';

    const tituloEl = document.createElement('h2');
    tituloEl.className = 'seccion-concursos__titulo';

    tituloEl.textContent = titulo;

    const contador = document.createElement('span');
    contador.className = 'seccion-concursos__contador';
    contador.textContent = ediciones.length;

    tituloEl.appendChild(contador);

    const lista = document.createElement('div');
    lista.className = 'seccion-concursos__lista';

    if (ediciones.length === 0) {

        const vacio = document.createElement('p');
        vacio.className = 'seccion-vacia';
        vacio.dataset.i18n = 'dashboard.seccion.vacia';
        vacio.textContent = t('dashboard.seccion.vacia');

        lista.appendChild(vacio);

    } else {

        ediciones.forEach(edicion => {
            lista.appendChild(crearTarjetaEdicion(edicion, estado));
        });

    }

    section.append(tituloEl, lista);

    return section;

}

// ============================================================
//  Render principal
// ============================================================

export function renderizarDashboard(ediciones, uid) {

    if (uid) _uid = uid;

    const contenedor = document.getElementById('contenedor-concursos');

    if (!contenedor) {
        console.error('[Dashboard] contenedor no encontrado');
        return;
    }

    const listaEdiciones = Array.isArray(ediciones) ? ediciones : [];
    ultimaListaEdiciones = [...listaEdiciones];

    const totalParticipaciones = listaEdiciones.length;
    const concursosUnicos = new Set(
        listaEdiciones
            .map(e => e?.concurso)
            .filter(Boolean)
    ).size;

    const elStatConcursos = document.getElementById('stat-concursos');
    const elStatParticipaciones = document.getElementById('stat-participaciones');

    if (elStatConcursos) elStatConcursos.textContent = concursosUnicos;
    if (elStatParticipaciones) elStatParticipaciones.textContent = totalParticipaciones;

    renderizarTarjetaResumenPerfil(listaEdiciones);
    contenedor.replaceChildren();

    // La card "Primeros pasos" va entre la tarjeta de perfil y ".dashboard-header"
    document.querySelector('.primeros-pasos-card')?.remove();
    const cardPP = crearTarjetaPrimerosPasos(_uid, listaEdiciones);
    if (cardPP) {
        const dashHeader = document.querySelector('.dashboard-header');
        dashHeader?.insertAdjacentElement('beforebegin', cardPP);
    }

    if (listaEdiciones.length === 0) {

        const vacio = document.createElement('div');
        vacio.className = 'estado-vacio';

        const icono = document.createElement('p');
        icono.className = 'estado-vacio__icono';
        icono.textContent = '📻';

        const texto = document.createElement('p');
        texto.className = 'estado-vacio__texto';
        texto.textContent = t('dashboard.vacio.texto');

        const subtexto = document.createElement('p');
        subtexto.className = 'estado-vacio__subtexto';
        subtexto.textContent = t('dashboard.vacio.subtexto');

        const btn = document.createElement('button');
        btn.className = 'btn-guardar estado-vacio__btn';
        btn.type = 'button';
        btn.textContent = t('dashboard.vacio.btn');
        btn.addEventListener('click', () => document.getElementById('btn-nuevo-concurso')?.click());

        vacio.append(icono, texto, subtexto, btn);
        contenedor.appendChild(vacio);

        return;

    }

    const { enCurso, proximos, finalizados } = clasificarEdiciones(listaEdiciones);

    enCurso.sort((a, b)     => (a.fechaFin    || 0) - (b.fechaFin    || 0));
    proximos.sort((a, b)    => (a.fechaInicio || 0) - (b.fechaInicio || 0));
    finalizados.sort((a, b) => (b.fechaFin    || 0) - (a.fechaFin    || 0));

    if (enCurso.length)     contenedor.appendChild(crearSeccion(t('dashboard.seccion.encurso'),    enCurso,    'en-curso'));
    if (proximos.length)    contenedor.appendChild(crearSeccion(t('dashboard.seccion.proximos'),   proximos,   'proximo'));
    if (finalizados.length) contenedor.appendChild(crearSeccion(t('dashboard.seccion.finalizados'), finalizados, 'finalizado'));

    registrarEventosTarjetas(contenedor, listaEdiciones);

}

export function actualizarTarjetaResumenPerfil() {
    renderizarTarjetaResumenPerfil(ultimaListaEdiciones);
}

// Re-renderizar dashboard completo al cambiar idioma
document.addEventListener('idioma:cambio', () => {
    if (!document.getElementById('contenedor-concursos')) return;
    renderizarDashboard(ultimaListaEdiciones, _uid);
});

export function getUltimasEdiciones() {
    return ultimaListaEdiciones;
}

export function limpiarDashboard() {
    const contenedor = document.getElementById('contenedor-concursos');
    if (contenedor) {
        contenedor.replaceChildren();
    }

    document.querySelector('.primeros-pasos-card')?.remove();
    ultimaListaEdiciones = [];
    limpiarTarjetaResumenPerfil();

    const elStatConcursos = document.getElementById('stat-concursos');
    const elStatParticipaciones = document.getElementById('stat-participaciones');

    if (elStatConcursos) elStatConcursos.textContent = '0';
    if (elStatParticipaciones) elStatParticipaciones.textContent = '0';
}

// ============================================================
//  Eventos
// ============================================================

let handlerTarjetas = null;

function registrarEventosTarjetas(contenedor, ediciones) {

    if (handlerTarjetas) {
        contenedor.removeEventListener('click', handlerTarjetas);
    }

    handlerTarjetas = async (evento) => {

        const btnEditar = evento.target.closest('.btn-accion--editar');
        const btnEliminar = evento.target.closest('.btn-accion--eliminar');
        const btnArchivo = evento.target.closest('.btn-archivo');

        if (btnEditar) {

            const id = btnEditar.dataset.id;
            const edicion = ediciones.find(e => e.id === id);

            if (edicion) abrirModalEditar(edicion);

            return;
        }

        if (btnEliminar) {

            const id = btnEliminar.dataset.id;
            const edicion = ediciones.find(e => e.id === id);

            if (!edicion) return;

            const confirmado = await mostrarConfirmacion(
                t('confirmacion.eliminar.titulo', { nombre: edicion.concurso, año: edicion.año }),
                t('confirmacion.eliminar.subtitulo')
            );

            if (confirmado) {

                const ok = await eliminarEdicion(id);

                if (ok) {

                    mostrarToast(
                        t('toast.concurso.eliminado', { nombre: edicion.concurso }),
                        'info'
                    );

                }

            }

            return;
        }

        if (btnArchivo) {

            const id = btnArchivo.dataset.id;
            const tipo = btnArchivo.dataset.tipo;
            const edicion = ediciones.find(e => e.id === id);

            if (edicion && tipo) {
                abrirModalArchivos(edicion, tipo);
            }

        }

    };

    contenedor.addEventListener('click', handlerTarjetas);

}